import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

/** One row = one cert version; selection drives “chain” panel by asset key */
const CERT_ROWS = [
  {
    certId: 'C-92341',
    asset: 'A-93284 / Loan-L-92189',
    assetKey: 'A-93284',
    type: 'Custody certificate',
    version: 'v3',
    expiration: 'Dec 31, 2028',
    currentCert: 'C-92341 at v3',
    prevCert: 'C-92140 (revoked)',
    supersededReason: 'Updated custody/owner',
    ruleChain: 'At-issue-version',
    bundleRef: 'EB-39213-91',
    holder: 'PNC-SPV-92314',
  },
  {
    certId: 'C-92140',
    asset: 'A-93284 / Loan-L-92189',
    assetKey: 'A-93284',
    type: 'Custody certificate',
    version: 'v2',
    expiration: 'Revoked',
    currentCert: 'C-92341 at v3',
    prevCert: 'C-92140 (revoked)',
    supersededReason: 'Updated custody/owner',
    ruleChain: 'At-issue-version',
    bundleRef: 'EB-39213-91',
    holder: 'PNC-SPV-92314',
  },
  {
    certId: 'C-18802-12',
    asset: 'A-18802 / Loan-L-18802',
    assetKey: 'A-18802',
    type: 'Participation attestation',
    version: 'v1',
    expiration: 'Jun 30, 2027',
    currentCert: 'C-18802-12 at v1',
    prevCert: '—',
    supersededReason: '—',
    ruleChain: 'Line A standard',
    bundleRef: 'EB-18802-01',
    holder: 'UMB Line A',
  },
];

export function CustodianCertificateHistoryPage() {
  const [selectedCertId, setSelectedCertId] = useState(CERT_ROWS[0].certId);
  const selected = useMemo(
    () => CERT_ROWS.find((c) => c.certId === selectedCertId) ?? CERT_ROWS[0],
    [selectedCertId],
  );

  return (
    <div className="page-stack">
      <header className="flex flex-col gap-3 border-b border-evault-border pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-evault-navy">
            Certificate history
          </h1>
          <p className="mt-1 max-w-2xl text-xs text-evault-muted">
            Active and superseded certificates — version chain, bundle
            references, and holder context.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-full border border-evault-border bg-white px-3 py-1 text-[11px] font-semibold text-evault-navy shadow-sm"
          >
            Generate
          </button>
          <button
            type="button"
            className="rounded-full bg-evault-navy px-3 py-1 text-[11px] font-semibold text-white shadow-sm"
          >
            Export
          </button>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="card-shell p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
            Active certificates
          </p>
          <p className="mt-1 text-2xl font-semibold text-evault-navy">311</p>
          <p className="mt-1 text-[10px] text-evault-muted">current</p>
        </div>
        <div className="card-shell p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
            Superseded
          </p>
          <p className="mt-1 text-2xl font-semibold text-evault-navy">46</p>
          <p className="mt-1 text-[10px] text-evault-muted">archived / old</p>
        </div>
        <div className="card-shell p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
            Serviced
          </p>
          <p className="mt-1 text-2xl font-semibold text-evault-navy">89</p>
          <p className="mt-1 text-[10px] text-evault-muted">last 90 days</p>
        </div>
        <div className="card-shell p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
            Revoked today
          </p>
          <p className="mt-1 text-2xl font-semibold text-evault-navy">6</p>
          <p className="mt-1 text-[10px] text-evault-muted">after expiration</p>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
        <div className="table-shell overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-[11px]">
            <thead className="bg-evault-surface text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
              <tr>
                <th className="px-3 py-2">Cert ID</th>
                <th className="px-3 py-2">Asset / entity</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Version</th>
                <th className="px-3 py-2">Current expiration</th>
              </tr>
            </thead>
            <tbody>
              {CERT_ROWS.map((c) => {
                const active = c.certId === selected.certId;
                return (
                  <tr
                    key={c.certId}
                    className={`cursor-pointer border-t border-evault-border ${
                      active ? 'bg-evault-surface' : 'hover:bg-evault-surface/60'
                    }`}
                    onClick={() => setSelectedCertId(c.certId)}
                  >
                    <td className="px-3 py-2 font-semibold text-evault-navy">
                      {c.certId}
                    </td>
                    <td className="px-3 py-2 text-evault-navy">{c.asset}</td>
                    <td className="px-3 py-2 text-evault-muted">{c.type}</td>
                    <td className="px-3 py-2 font-medium text-evault-navy">
                      {c.version}
                    </td>
                    <td className="px-3 py-2 text-evault-muted">{c.expiration}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <aside className="card-shell flex flex-col gap-3 p-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
              Selected certificate chain
            </p>
            <p className="mt-1 text-sm font-semibold text-evault-navy">
              Asset: {selected.asset}
            </p>
          </div>
          <dl className="space-y-2 text-[11px]">
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Current certificate
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.currentCert}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Prev certificate
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.prevCert}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Superseded reason
              </dt>
              <dd className="mt-0.5 text-evault-navy">
                {selected.supersededReason}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Active rule chain
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.ruleChain}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Bundle ref
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.bundleRef}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Current holder
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.holder}</dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-2 border-t border-evault-border pt-3">
            <button
              type="button"
              className="rounded-full bg-evault-teal-deep px-3 py-1 text-[11px] font-semibold text-white"
            >
              View current
            </button>
            <button
              type="button"
              className="rounded-full border border-evault-border bg-white px-3 py-1 text-[11px] font-semibold text-evault-navy"
            >
              Refresh
            </button>
            <button
              type="button"
              className="rounded-full border border-evault-border bg-white px-3 py-1 text-[11px] font-semibold text-evault-navy"
            >
              Archive
            </button>
          </div>
          <div className="border-t border-evault-border pt-3 text-[11px]">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
              Related
            </p>
            <ul className="mt-2 space-y-1.5 text-evault-teal-deep">
              <li>
                <Link to="/custodian/reports" className="hover:underline">
                  History / audit trail
                </Link>
              </li>
              <li>
                <Link
                  to={`/custodian/assets/${selected.assetKey}`}
                  className="hover:underline"
                >
                  Version explorer
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
